/**
 * CityPulse — hero widget pinned above every city article.
 *
 * Live at-a-glance: local time, current weather, top-1 news headline
 * and the busiest POI category. All feeds are best-effort — each
 * tile fades to a neutral state if its source is offline so the
 * widget never blocks page render.
 *
 * Data sources (all free, no keys):
 *   • Time     — Intl.DateTimeFormat with a heuristic timezone from
 *                the location coordinates (falls back to browser TZ).
 *   • Weather  — Open-Meteo `forecast` endpoint (current block).
 *   • News     — /api/news/local/{slug} (already server-cached 1h).
 *   • POIs     — /api/geo/places/directory/{slug} — top category by
 *                count.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Clock, Cloud, Sun, CloudRain, CloudSnow, CloudFog, CloudLightning, Wind,
  Newspaper, Building2, Landmark, MapPin, HeartPulse, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Map Open-Meteo WMO weather codes → friendly label + icon.
// https://open-meteo.com/en/docs (weather_code table)
function weatherFrom(code) {
  if (code == null) return { label: "—", Icon: Cloud };
  if (code === 0) return { label: "Clear", Icon: Sun };
  if (code <= 3) return { label: "Partly cloudy", Icon: Cloud };
  if (code <= 48) return { label: "Fog", Icon: CloudFog };
  if (code <= 57) return { label: "Drizzle", Icon: CloudRain };
  if (code <= 67) return { label: "Rain", Icon: CloudRain };
  if (code <= 77) return { label: "Snow", Icon: CloudSnow };
  if (code <= 82) return { label: "Showers", Icon: CloudRain };
  if (code <= 86) return { label: "Snow showers", Icon: CloudSnow };
  if (code <= 99) return { label: "Thunderstorm", Icon: CloudLightning };
  return { label: "Weather", Icon: Cloud };
}

const CATEGORY_ICON = {
  hospitals:   HeartPulse,
  schools:     Building2,
  colleges:    Building2,
  worship:     Landmark,
  parks:       MapPin,
  charities:   MapPin,
  food_banks:  MapPin,
  monuments:   Landmark,
  museums:     Landmark,
  attractions: MapPin,
  libraries:   Building2,
};

function useTick(ms = 30000) {
  const [, setN] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(iv);
  }, [ms]);
}

export default function CityPulse({ slug, name, kind, lat, lng, timezone, state, country }) {
  const [weather, setWeather] = useState(null);
  const [news, setNews] = useState(null);
  const [poi, setPoi] = useState(null);
  const [loading, setLoading] = useState(true);
  useTick(30000);

  // Weather via Open-Meteo — free, no key. Skip when no coords.
  useEffect(() => {
    let alive = true;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setWeather(null);
      return;
    }
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code,wind_speed_10m&temperature_unit=celsius&timezone=auto`;
    axios.get(url, { timeout: 6000 })
      .then((r) => alive && setWeather(r.data?.current || null))
      .catch(() => alive && setWeather(null));
    return () => { alive = false; };
  }, [lat, lng]);

  useEffect(() => {
    let alive = true;
    if (!slug) return;
    Promise.all([
      axios.get(`${API}/api/news/local/${slug}`, { timeout: 8000 }).catch(() => null),
      axios.get(`${API}/api/geo/places/directory/${slug}`, { timeout: 8000 }).catch(() => null),
    ]).then(([nRes, pRes]) => {
      if (!alive) return;
      setNews(nRes?.data?.items?.[0] || null);
      const cats = pRes?.data?.categories || [];
      const top = cats.sort((a, b) => (b.count || 0) - (a.count || 0))[0];
      setPoi(top || null);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [slug]);

  // Local time — best-effort timezone from prop, else browser default.
  const localTime = useMemo(() => {
    try {
      const opts = { hour: "2-digit", minute: "2-digit", weekday: "short", month: "short", day: "numeric" };
      if (timezone) opts.timeZone = timezone;
      return new Intl.DateTimeFormat(undefined, opts).format(new Date());
    } catch {
      return new Date().toLocaleTimeString();
    }
  }, [timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const { label: wLabel, Icon: WIcon } = weatherFrom(weather?.weather_code);
  const temp = weather?.temperature_2m;
  const wind = weather?.wind_speed_10m;
  const CategoryIcon = poi ? (CATEGORY_ICON[poi.category] || MapPin) : MapPin;

  // For counties, the 4th tile falls back to "Explore the parent
  // state" because counties rarely index direct POIs themselves —
  // the cities inside them do.
  const isCounty = (kind || "").toLowerCase() === "county";
  const stateSlug = isCounty ? slug.split("/").slice(0, 2).join("/") : null;

  return (
    <div
      className="mb-6 rounded-2xl border-2 overflow-hidden"
      style={{
        borderColor: "#DBEAFE",
        background: "linear-gradient(135deg, #EFF6FF 0%, #F0FDFA 100%)",
      }}
      data-testid="city-pulse"
    >
      <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: "#DBEAFE" }}>
        <div className="text-[10px] font-mono uppercase tracking-widest text-blue-800 inline-flex items-center gap-1.5">
          <HeartPulse className="w-3 h-3" /> {name} · City Pulse
        </div>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-50" />}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0" style={{ borderColor: "#DBEAFE" }}>
        {/* Local time */}
        <div className="p-3" data-testid="pulse-time">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Local time
          </div>
          <div className="mt-1 font-display text-lg leading-tight text-slate-900">
            {localTime.split(",")[0]}
          </div>
          <div className="text-xs opacity-70 truncate">{localTime.split(",").slice(1).join(",").trim()}</div>
        </div>

        {/* Weather */}
        <div className="p-3" data-testid="pulse-weather">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 flex items-center gap-1">
            <WIcon className="w-3 h-3" /> Weather
          </div>
          {weather ? (
            <>
              <div className="mt-1 font-display text-lg leading-tight text-slate-900">
                {typeof temp === "number" ? `${Math.round(temp)}°C` : "—"}
              </div>
              <div className="text-xs opacity-70 truncate">
                {wLabel}
                {typeof wind === "number" && (
                  <span className="inline-flex items-center gap-0.5 ml-1.5 opacity-70">
                    <Wind className="w-3 h-3" /> {Math.round(wind)} km/h
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="mt-1 text-sm opacity-60">Not available</div>
          )}
        </div>

        {/* Top news */}
        <div className="p-3" data-testid="pulse-news">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 flex items-center gap-1">
            <Newspaper className="w-3 h-3" /> Top headline
          </div>
          {news ? (
            <a
              href={news.url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block font-medium text-sm leading-snug text-slate-900 hover:underline line-clamp-2"
              data-testid="pulse-news-link"
            >
              {news.title}
            </a>
          ) : (
            <div className="mt-1 text-sm opacity-60">{loading ? "Loading…" : "No headlines"}</div>
          )}
          {news?.source && <div className="text-xs opacity-70 mt-1 truncate">{news.source}</div>}
        </div>

        {/* Top POI category — or, for counties with no direct POIs,
            a nav tile back up to the parent state. */}
        <div className="p-3" data-testid="pulse-poi">
          {isCounty && !poi ? (
            <>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 flex items-center gap-1">
                <Landmark className="w-3 h-3" /> Parent state
              </div>
              {stateSlug ? (
                <a
                  href={`/l/${stateSlug}`}
                  className="mt-1 block font-display text-lg leading-tight text-slate-900 hover:underline"
                  data-testid="pulse-state-link"
                >
                  Explore {state || stateSlug.split("/").pop().replace(/-/g, " ")}
                </a>
              ) : (
                <div className="mt-1 text-sm opacity-60">—</div>
              )}
              <div className="text-xs opacity-70">Cities, towns and counties</div>
            </>
          ) : (
            <>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 flex items-center gap-1">
                <CategoryIcon className="w-3 h-3" /> Busiest category
              </div>
              {poi ? (
                <a
                  href={`/places/${slug}/${poi.category}`}
                  className="mt-1 block font-display text-lg leading-tight text-slate-900 hover:underline"
                  data-testid="pulse-poi-link"
                >
                  {poi.label}
                </a>
              ) : (
                <div className="mt-1 text-sm opacity-60">{loading ? "Loading…" : "No POIs yet"}</div>
              )}
              {poi && <div className="text-xs opacity-70">{poi.count} places indexed</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
